import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { decodeQuizData, calculateScore } from '../utils/helpers';
import { CORE_QUESTIONS } from '../data/quizData';
import { QuestionCard } from '../components/QuestionCard';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiAward } from 'react-icons/fi';
import supabase from '../supabase/supabase';
import { Footer } from '../components/Footer';

export const TakeChallenge = () => {
  const { data } = useParams();
  const navigate = useNavigate();
  const [creatorData, setCreatorData] = useState(null);
  const [challengerName, setChallengerName] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [isFinishing, setIsFinishing] = useState(false);
  const [recentScores, setRecentScores] = useState([]);

  const logoUrl = "https://media-manager-c.questera.ai/greta-media/5aef0a8df517b865df3c53c45a2fd1ff726cf5f808670b49edd6820a48780cae68c41714c5c531fe697e77aede0c0572/images/aW1hZ2UvcG5n/b4fb7c8e8b2fa91d7665d00ad27aec75.png";

  useEffect(() => {
    if (data) {
      const decoded = decodeQuizData(data);
      if (decoded) {
        setCreatorData(decoded);
        fetchRecentScores(data);
      } else {
        navigate('/');
      }
    }
  }, [data, navigate]);

  const fetchRecentScores = async (shareCode) => {
    try {
      const { data: scores, error } = await supabase
        .from('dykm_scores_1774355620612')
        .select('*')
        .eq('share_code', shareCode)
        .order('score', { ascending: false })
        .limit(3);

      if (!error && scores) {
        setRecentScores(scores);
      }
    } catch (err) {
      console.error("TakeChallenge fetch failed:", err);
    }
  };

  const handleStart = (e) => {
    e.preventDefault();
    if (challengerName.trim()) setHasStarted(true);
  };

  const handleAnswer = async (optionIndex) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);

    if (currentIndex < CORE_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentIndex(prev => prev + 1), 400);
    } else {
      setIsFinishing(true);
      const scoreValue = calculateScore(creatorData.answers, newAnswers);
      
      try {
        const { error } = await supabase
          .from('dykm_scores_1774355620612')
          .insert([
            {
              share_code: data,
              challenger_name: challengerName.trim(),
              score: scoreValue,
              total_questions: CORE_QUESTIONS.length
            }
          ]);
        
        if (error) throw error;

        setTimeout(() => {
          navigate('/result', { 
            state: { 
              creatorName: creatorData.name, 
              challengerName, 
              score: scoreValue, 
              total: CORE_QUESTIONS.length 
            } 
          });
        }, 800);
      } catch (error) {
        console.error("Error saving score:", error.message);
        // Navigate anyway to show user their score even if DB fails
        navigate('/result', { 
          state: { creatorName: creatorData.name, challengerName, score: scoreValue, total: CORE_QUESTIONS.length } 
        });
      }
    }
  };

  if (!creatorData) return null;

  if (!hasStarted) {
    return (
      <div className="min-h-screen flex flex-col relative bg-[#012b58]">
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md text-center">
            <div className="mb-8 flex justify-center">
              <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" />
            </div>
            
            <div className="glass-panel p-8 rounded-3xl mb-6 border border-white/10">
              <h1 className="text-3xl font-bold mb-2 text-white">
                <span className="text-[#d2ff02]">{creatorData.name}</span> challenged you!
              </h1>
              <p className="text-slate-300 mb-8">Think you know them? Prove it.</p>
              
              <form onSubmit={handleStart} className="space-y-4">
                <input
                  type="text" value={challengerName}
                  onChange={(e) => setChallengerName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={15}
                  className="w-full bg-[#012b58]/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#0094f7]"
                  required
                />
                <Button type="submit" className="w-full h-14">Accept Challenge</Button>
              </form>
            </div>

            {recentScores.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 text-left"
              >
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiUsers} className="text-[#0094f7]" />
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Top Players</span>
                </div>
                <div className="space-y-2">
                  {recentScores.map((score) => (
                    <div key={score.id} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300">{score.challenger_name}</span>
                      <div className="flex items-center gap-1 font-bold text-[#d2ff02]">
                        <span>{score.score}/10</span>
                        <SafeIcon icon={FiAward} className="text-xs" />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#012b58]">
      <div className="flex-1 flex flex-col p-6 max-w-2xl mx-auto w-full">
        <div className="flex justify-between items-center pt-4 mb-4">
          <img src={logoUrl} alt="Logo" className="h-6 w-auto object-contain" />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Playing: {creatorData.name}'s Quiz</span>
        </div>
        
        <div className="pb-12">
          <ProgressBar current={isFinishing ? CORE_QUESTIONS.length : currentIndex} total={CORE_QUESTIONS.length} />
        </div>

        <div className="flex-1 flex flex-col justify-center relative">
          <AnimatePresence mode="wait">
            {!isFinishing ? (
              <QuestionCard 
                key={currentIndex}
                question={CORE_QUESTIONS[currentIndex]}
                currentIndex={currentIndex}
                total={CORE_QUESTIONS.length}
                onSelect={handleAnswer}
              />
            ) : (
              <div className="text-center text-white">
                <h2 className="text-2xl font-bold mb-2">Calculating your score...</h2>
                <div className="w-8 h-8 border-4 border-[#d2ff02] border-t-transparent rounded-full animate-spin mx-auto mt-4" />
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </div>
  );
};